import type { CoefficientDto } from '@glory/contracts';
import { formatMoney, formatMultiplier, formatPercent, t } from '@glory/i18n';
import {
  type BasisPoints,
  bp,
  BP_SCALE,
  bpToDecimal,
  kopecksFromRubles,
  roundHalfUpToKopeck,
} from '@glory/kernel';
import {
  COEFFICIENT_DESCRIPTION_MAX,
  COEFFICIENT_NAME_MAX,
  MULTIPLIER_MAX,
  MULTIPLIER_MIN,
} from '@glory/pricing/domain';
import {
  Button,
  Callout,
  Card,
  ContentSwap,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  IconButton,
  InfoHint,
  MultiplierField,
  notify,
  PageHeader,
  SkeletonRows,
  SwitchField,
  TextAreaField,
  TextField,
  toastUndoable,
} from '@glory/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Archive, MoreHorizontal, Pencil, Plus, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { type SubmitEvent, useState } from 'react';

import { client, orpc } from '../../../lib/orpc.ts';
import { requireSettingsPermission, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/settings/coefficients')({
  beforeLoad: requireSettingsPermission('pricing.read'),
  component: CoefficientsPage,
});

interface OpenDialog {
  readonly coefficient: CoefficientDto | null;
}

const HUNDRED_THOUSAND = kopecksFromRubles(100_000);

/** «надбавка 30 %» или «скидка 10 %»; ×1 — без подписи. */
function effectOf(multiplier: BasisPoints): string | null {
  if (multiplier === BP_SCALE) return null;
  const percent = formatPercent(bp(Math.abs(multiplier - BP_SCALE)));
  return multiplier > BP_SCALE
    ? t('coefficients.raise', { percent })
    : t('coefficients.discount', { percent });
}

const useRefresh = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: orpc.pricing.coefficients.key() });
};

function CoefficientsPage() {
  const { can } = usePermissions();
  const canRead = can('pricing.read');
  const canManage = can('pricing.manage');
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const list = useQuery({
    ...orpc.pricing.coefficients.list.queryOptions({ input: { includeArchived: showArchived } }),
    enabled: canRead,
  });
  const items = list.data?.items ?? [];
  const active = items.filter((c) => !c.archived);
  const archived = items.filter((c) => c.archived);
  const create = () => {
    setDialog({ coefficient: null });
  };
  // Что под заголовком: загрузка, пустой список и список проявляются друг из друга, без скачка высоты.
  const phase = !canRead
    ? 'denied'
    : list.isError
      ? 'error'
      : list.isPending
        ? 'loading'
        : active.length === 0
          ? 'empty'
          : 'ready';

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2 whitespace-normal">
            {t('coefficients.title')}
            <InfoHint hint={{ text: t('coefficients.hint'), example: t('coefficients.hintExample') }} />
          </span>
        }
        actions={
          canManage && active.length > 0 ? (
            <Button variant="primary" onClick={create}>
              <Plus /> {t('coefficients.create')}
            </Button>
          ) : null
        }
      />
      <p className="-mt-2 text-muted">{t('coefficients.lead')}</p>

      <ContentSwap swapKey={phase}>
        {!canRead ? (
          <Callout tone="strong">{t('coefficients.noAccess')}</Callout>
        ) : list.isError ? (
          <Callout>{list.error.message}</Callout>
        ) : list.isPending ? (
          <SkeletonRows rows={4} />
        ) : active.length === 0 ? (
          <Card>
            <EmptyState
              icon={<SlidersHorizontal />}
              title={t('coefficients.emptyTitle')}
              description={t('coefficients.emptyText')}
              action={
                canManage ? (
                  <Button variant="primary" onClick={create}>
                    <Plus /> {t('coefficients.create')}
                  </Button>
                ) : null
              }
            />
          </Card>
        ) : (
          <Card>
            <ul className="flex flex-col divide-y divide-border-soft">
              {active.map((coefficient) => (
                <CoefficientRow
                  key={coefficient.id}
                  coefficient={coefficient}
                  canManage={canManage}
                  onEdit={() => {
                    setDialog({ coefficient });
                  }}
                />
              ))}
            </ul>
          </Card>
        )}
      </ContentSwap>

      {canManage && canRead ? (
        <div className="flex flex-col gap-2">
          <SwitchField
            inline
            label={t('coefficients.showArchived')}
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          {showArchived && archived.length > 0 ? (
            <Card>
              <ul className="flex flex-col divide-y divide-border-soft">
                {archived.map((coefficient) => (
                  <ArchivedRow key={coefficient.id} coefficient={coefficient} />
                ))}
              </ul>
            </Card>
          ) : null}
          {showArchived && archived.length === 0 && !list.isPending ? (
            <p className="text-small text-muted">{t('coefficients.archiveEmpty')}</p>
          ) : null}
        </div>
      ) : null}

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
      >
        {dialog ? (
          <CoefficientDialog
            coefficient={dialog.coefficient}
            onDone={() => {
              setDialog(null);
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function CoefficientRow({
  coefficient,
  canManage,
  onEdit,
}: {
  coefficient: CoefficientDto;
  canManage: boolean;
  onEdit: () => void;
}) {
  const refresh = useRefresh();
  const archive = useMutation({
    mutationFn: () =>
      client.pricing.coefficients.archive({ id: coefficient.id, version: coefficient.version }),
    onSuccess: async () => {
      await refresh();
      toastUndoable({
        message: t('coefficients.archived', { name: coefficient.name }),
        onUndo: () =>
          void client.pricing.coefficients
            .restore({ id: coefficient.id, version: coefficient.version + 1 })
            .then(refresh),
      });
    },
    onError: (error) => {
      notify(error.message);
    },
  });
  const effect = effectOf(coefficient.multiplier);
  const main = (
    <>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold">{coefficient.name}</span>
        {coefficient.description ? (
          <span className="line-clamp-2 text-caption text-muted">{coefficient.description}</span>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end">
        <span className="font-display text-title font-bold tabular-nums">
          {formatMultiplier(coefficient.multiplier)}
        </span>
        {effect ? <span className="text-caption text-muted">{effect}</span> : null}
      </div>
    </>
  );
  return (
    <li className="flex items-center gap-2 px-4 py-2.5">
      {canManage ? (
        <button
          type="button"
          className="-mx-1.5 flex min-w-0 flex-1 items-center gap-3 rounded-md px-1.5 py-0.5 text-left hover:bg-surface-2"
          onClick={onEdit}
        >
          {main}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">{main}</div>
      )}
      {canManage ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton label={t('coefficients.more')} icon={<MoreHorizontal />} size="sm" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem icon={<Pencil />} onSelect={onEdit}>
              {t('coefficients.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              icon={<Archive />}
              onSelect={() => {
                archive.mutate();
              }}
            >
              {t('coefficients.archive')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </li>
  );
}

function ArchivedRow({ coefficient }: { coefficient: CoefficientDto }) {
  const refresh = useRefresh();
  const restore = useMutation({
    mutationFn: () =>
      client.pricing.coefficients.restore({ id: coefficient.id, version: coefficient.version }),
    onSuccess: async () => {
      notify(t('coefficients.restored', { name: coefficient.name }));
      await refresh();
    },
  });
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <span className="min-w-0 flex-1 truncate text-muted">{coefficient.name}</span>
      <span className="text-muted tabular-nums">{formatMultiplier(coefficient.multiplier)}</span>
      {restore.isError ? <span className="text-caption text-danger">{restore.error.message}</span> : null}
      <Button
        size="sm"
        loading={restore.isPending}
        onClick={() => {
          restore.mutate();
        }}
      >
        <RotateCcw /> {t('coefficients.restore')}
      </Button>
    </li>
  );
}

function CoefficientDialog({
  coefficient,
  onDone,
}: {
  coefficient: CoefficientDto | null;
  onDone: () => void;
}) {
  const refresh = useRefresh();
  const [name, setName] = useState(coefficient?.name ?? '');
  const [multiplier, setMultiplier] = useState<BasisPoints | null>(coefficient?.multiplier ?? null);
  const [description, setDescription] = useState(coefficient?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (input: { name: string; multiplier: BasisPoints; description: string | null }) =>
      coefficient
        ? client.pricing.coefficients.update({ ...input, id: coefficient.id, version: coefficient.version })
        : client.pricing.coefficients.create(input),
    onSuccess: async (saved) => {
      notify(t('coefficients.saved', { name: saved.name }));
      await refresh();
      onDone();
    },
  });
  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (name.trim() === '' || multiplier === null) {
      setError(t('coefficients.required'));
      return;
    }
    save.mutate({ name, multiplier, description: description.trim() === '' ? null : description });
  };
  return (
    <DialogContent title={coefficient ? t('coefficients.editTitle') : t('coefficients.createTitle')}>
      <form className="flex flex-col gap-3" noValidate onSubmit={submit}>
        <TextField
          label={t('coefficients.name')}
          placeholder={t('coefficients.namePlaceholder')}
          required
          autoFocus
          maxLength={COEFFICIENT_NAME_MAX}
          value={name}
          onValueChange={(value) => {
            setName(value);
            setError(null);
          }}
        />
        <MultiplierField
          label={t('coefficients.multiplier')}
          hint={{ text: t('coefficients.multiplierHint') }}
          required
          value={multiplier}
          min={bp(MULTIPLIER_MIN)}
          max={bp(MULTIPLIER_MAX)}
          onValueChange={(value) => {
            setMultiplier(value);
            setError(null);
          }}
          description={
            multiplier === null
              ? undefined
              : t('coefficients.example', {
                  amount: formatMoney(roundHalfUpToKopeck(bpToDecimal(multiplier).times(HUNDRED_THOUSAND))),
                })
          }
        />
        <TextAreaField
          label={t('coefficients.description')}
          placeholder={t('coefficients.descriptionPlaceholder')}
          maxLength={COEFFICIENT_DESCRIPTION_MAX}
          value={description}
          onValueChange={setDescription}
        />
        {(error ?? save.error?.message) ? <Callout>{error ?? save.error?.message}</Callout> : null}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t('coefficients.save')}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
